import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { XIcon } from '../ui/Icons';
import { UnifiedFilter } from '../generic/FilterSection';
import { formatDate } from '../../config/appConfig';
import { getDateRangeForPreset } from './dateFilterHelpers';

interface ActiveFiltersDisplayProps {
  unifiedFilters: Record<string, UnifiedFilter>;
  filterFields: Array<{
    key: string;
    label: string;
    type?: 'text' | 'date' | 'number' | 'boolean' | 'select';
  }>;
  onRemoveFilter: (key: string) => void;
  onClearAll: () => void;
  filteredCount?: number;
}

function isEpochStart(d: Date): boolean {
  return d.getUTCFullYear() === 1970 && d.getUTCMonth() === 0 && d.getUTCDate() === 1;
}

/** Get the resolved date range as formatted string for display (e.g. "until 9 Feb 2026" or "1 Mar – 31 Mar 2026") */
function getDateRangeDisplayString(filter: UnifiedFilter): string | null {
  if (filter.type !== 'dateRange' || !filter.preset || filter.preset === 'all') return null;
  let start: Date | null = null;
  let end: Date | null = null;
  if (filter.from || filter.to) {
    const parse = (s: string) => new Date(s.includes('T') ? s : s + 'T00:00:00Z');
    start = filter.from ? parse(filter.from) : null;
    end = filter.to ? parse(filter.to) : null;
  } else if (filter.preset !== 'custom') {
    const range = getDateRangeForPreset(filter.preset);
    start = range.start;
    end = range.end;
  }
  if (!start && !end) return null;
  const startStr = start ? formatDate(start) : null;
  const endStr = end ? formatDate(end) : null;
  const noStart = !start || (start && isEpochStart(start));
  if (noStart && endStr) return `until ${endStr}`;
  if (startStr && !end) return `from ${startStr}`;
  if (startStr && endStr && !noStart) return startStr === endStr ? startStr : `${startStr} – ${endStr}`;
  if (endStr) return `until ${endStr}`;
  return startStr;
}

const ActiveFiltersDisplay: React.FC<ActiveFiltersDisplayProps> = ({
  unifiedFilters,
  filterFields,
  onRemoveFilter,
  onClearAll,
  filteredCount,
}) => {
  const { colors } = useTheme();
  // Generate natural language filter summary
  const generateFilterSummary = (): string => {
    const filterDescriptions: string[] = [];

    Object.entries(unifiedFilters).forEach(([key, filter]) => {
      if (!filter) return;
      
      const field = filterFields.find((f) => f.key === key);
      if (!field) return;

      let description = '';

      switch (filter.type) {
        case 'multiSelect':
          if (filter.values && filter.values.length > 0) {
            if (filter.values.length === 1) {
              description = `${field.label} is ${filter.values[0]}`;
            } else if (filter.values.length <= 3) {
              // Show all values for 2-3 items
              const lastValue = filter.values[filter.values.length - 1];
              const otherValues = filter.values.slice(0, -1).join(', ');
              description = `${field.label} is ${otherValues} or ${lastValue}`;
            } else {
              description = `${field.label} is one of ${filter.values.length} values`;
            }
          }
          break;

        case 'dateRange':
          // Check if filter is active (preset exists and not 'all'; from/to may be missing for saved presets)
          if (filter.preset && filter.preset !== 'all' && (filter.from || filter.to || filter.preset !== 'custom')) {
            const dateRangeStr = getDateRangeDisplayString(filter);
            if (filter.preset !== 'custom') {
              const presetName = filter.preset
                .charAt(0)
                .toUpperCase() +
                filter.preset
                  .slice(1)
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/([a-z])([A-Z])/g, '$1 $2');
              description = dateRangeStr
                ? `${field.label} is ${presetName} (${dateRangeStr})`
                : `${field.label} is ${presetName}`;
            } else {
              if (filter.from && filter.to) {
                const fromText = formatDate(filter.from);
                const toText = formatDate(filter.to);
                description = fromText === toText
                  ? `${field.label} is ${fromText}`
                  : `${field.label} is between ${fromText} and ${toText}`;
              } else if (filter.from) {
                description = `${field.label} is from ${formatDate(filter.from)}`;
              } else if (filter.to) {
                description = `${field.label} is until ${formatDate(filter.to)}`;
              } else if (dateRangeStr) {
                description = `${field.label}: ${dateRangeStr}`;
              }
            }
          }
          break;

        case 'numericRange':
          if (filter.min !== undefined && filter.max !== undefined) {
            if (filter.min === filter.max) {
              description = `${field.label} is ${filter.min}`;
            } else {
              description = `${field.label} is between ${filter.min} and ${filter.max}`;
            }
          } else if (filter.min !== undefined) {
            description = `${field.label} is at least ${filter.min}`;
          } else if (filter.max !== undefined) {
            description = `${field.label} is at most ${filter.max}`;
          }
          break;

        case 'boolean':
          if (filter.value !== undefined) {
            description = `${field.label} is ${filter.value ? 'Yes' : 'No'}`;
          }
          break;

        case 'text':
          if (filter.textValue) {
            description = `${field.label} contains '${filter.textValue}'`;
          }
          break;

        case 'select':
          if (filter.textValue) {
            description = `${field.label} is ${filter.textValue}`;
          }
          break;
      }

      if (description) {
        filterDescriptions.push(description);
      }
    });

    if (filterDescriptions.length === 0) {
      return '';
    }

    // Build the summary text
    let summary = '';
    if (filteredCount !== undefined) {
      summary = `Showing ${filteredCount} record${filteredCount !== 1 ? 's' : ''} where `;
    } else {
      summary = 'Filtering by ';
    }

    if (filterDescriptions.length === 1) {
      summary += filterDescriptions[0];
    } else if (filterDescriptions.length <= 3) {
      // Show all filters for 2-3 items
      const lastFilter = filterDescriptions[filterDescriptions.length - 1];
      const otherFilters = filterDescriptions.slice(0, -1).join(', ');
      summary += `${otherFilters} and ${lastFilter}`;
    } else {
      // Truncate if more than 3 filters
      const visibleFilters = filterDescriptions.slice(0, 2).join(', ');
      const remainingCount = filterDescriptions.length - 2;
      summary += `${visibleFilters} and ${remainingCount} more filter${remainingCount !== 1 ? 's' : ''}`;
    }

    return summary;
  };

  const filterSummary = generateFilterSummary();
  const activeFilters = Object.entries(unifiedFilters)
    .filter(([_, filter]) => filter !== null)
    .map(([key, filter]) => {
      const field = filterFields.find((f) => f.key === key);
      if (!field || !filter) return null;

      let displayText = '';
      let displayValue = '';

      switch (filter.type) {
        case 'multiSelect':
          if (filter.values && filter.values.length > 0) {
            displayValue = filter.values.length === 1 
              ? filter.values[0] 
              : `${filter.values.length} selected`;
            displayText = `${field.label}: ${displayValue}`;
          }
          break;

        case 'dateRange':
          if (filter.preset && filter.preset !== 'all') {
            const presetLabel = filter.preset !== 'custom'
              ? filter.preset.charAt(0).toUpperCase() + filter.preset.slice(1).replace(/([A-Z])/g, ' $1')
              : '';
            const dateRangeStr = getDateRangeDisplayString(filter);
            if (filter.preset !== 'custom') {
              displayValue = dateRangeStr ? `${presetLabel} (${dateRangeStr})` : presetLabel;
            } else {
              displayValue = dateRangeStr || (filter.from && filter.to ? `${formatDate(filter.from)} – ${formatDate(filter.to)}` : '');
            }
            displayText = `${field.label}: ${displayValue}`;
          }
          break;

        case 'numericRange':
          if (filter.min !== undefined || filter.max !== undefined) {
            const minText = filter.min !== undefined ? filter.min.toString() : '...';
            const maxText = filter.max !== undefined ? filter.max.toString() : '...';
            displayValue = `${minText} - ${maxText}`;
            displayText = `${field.label}: ${displayValue}`;
          }
          break;

        case 'boolean':
          if (filter.value !== undefined) {
            displayValue = filter.value ? 'Yes' : 'No';
            displayText = `${field.label}: ${displayValue}`;
          }
          break;

        case 'text':
        case 'select':
          if (filter.textValue) {
            displayValue = filter.textValue;
            displayText = `${field.label}: ${displayValue}`;
          }
          break;
      }

      return displayText ? { key, label: field.label, displayText, displayValue } : null;
    })
    .filter((item): item is { key: string; label: string; displayText: string; displayValue: string } => item !== null);

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg mb-3">
      {/* Filter Summary Text */}
      {filterSummary && (
        <div className="px-4 pt-3 pb-2 border-b border-blue-200">
          <p className={`text-sm ${colors.textSecondary} leading-relaxed`}>
            <svg 
              className="w-4 h-4 inline-block mr-1.5 text-blue-600 align-text-bottom" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {filterSummary}
          </p>
        </div>
      )}

      {/* Filter Chips */}
      <div className="flex flex-wrap items-center gap-2 p-3">
      
      {activeFilters.map((filter) => (
        <div
          key={filter.key}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${colors.surface} border border-blue-300 rounded-md text-sm ${colors.textSecondary} hover:bg-blue-50 transition-colors`}
        >
          <span className="font-medium text-blue-700">{filter.label}:</span>
          <span className={colors.text}>{filter.displayValue}</span>
          <button
            onClick={() => onRemoveFilter(filter.key)}
            className="ml-1 p-0.5 hover:bg-blue-100 rounded transition-colors"
            title={`Remove ${filter.label} filter`}
            aria-label={`Remove ${filter.label} filter`}
          >
            <XIcon className={`w-3.5 h-3.5 ${colors.textMuted} hover:text-red-600`} />
          </button>
        </div>
      ))}

        {activeFilters.length > 1 && (
          <button
            onClick={onClearAll}
            className="ml-auto px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-300 rounded-md transition-colors"
          >
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default ActiveFiltersDisplay;

